import classNames from "classnames";
import React, { useState } from "react";
import { default as Spinner } from "react-spinkit";
import { t } from "ttag";

import { IBasket, IWishListLine } from "../../../models/catalogue.interfaces";
import { IProductAPIURL } from "../../../models/nominals";
import { trackAddToBasketEvent } from "../../../utils/analytics";
import { BasketLineImage } from "./BasketLineImage";
import { BasketLineOptions } from "./BasketLineOptions";
import { BasketLineTitle } from "./BasketLineTitle";
import { WishlistLinePrice } from "./WishlistLinePrice";
import { IBasketLineBaseProps } from "./utils";

const RemoveButton = (props: { onRemoveLine: () => Promise<void> }) => (
    <button
        onClick={props.onRemoveLine}
        className="basket-line__details__button basket-line__details__button--remove al-basket-line__button--remove"
    >
        [{t`Remove from List`}]
    </button>
);

const AddToBasketButton = (props: {
    onAddToBasket: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) => (
    <button
        onClick={props.onAddToBasket}
        className="basket-line__details__button al-checkout__add-back-button"
    >
        {t`Move to Cart`}
    </button>
);

interface IProps extends IBasketLineBaseProps<IWishListLine> {
    addBasketLine?: (
        productURL: IProductAPIURL,
        qty?: number,
    ) => Promise<IBasket>;
    removeWishlistLine: (lineURL: string) => Promise<IBasket>;
}

export const WishListLine = (props: IProps): React.ReactNode => {
    const [isOperationPending, setIsOperationPending] = useState(false);

    const onAddToBasket = async (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        if (!props.addBasketLine) {
            return;
        }
        event.preventDefault();
        setIsOperationPending(true);
        await props.addBasketLine(props.line.product.url);
        trackAddToBasketEvent(props.line.product);
        setIsOperationPending(false);
    };

    const onRemoveLine = async () => {
        setIsOperationPending(true);
        await props.removeWishlistLine(props.line.url);
        setIsOperationPending(false);
    };

    const product = props.line.product;
    const loadingIndicatorClasses = classNames({
        "basket-line__details__controls-loading": true,
        "basket-line__details__controls-loading--active": isOperationPending,
    });
    return (
        <div
            className="basket-line basket-line--wishlist"
            role="group"
            // TODO(i18n): Use capitalized "Product" for consistency with aria-label conventions
            aria-label={t`product`}
        >
            <div className="basket-line__graphic">
                <BasketLineImage product={product} />
            </div>
            <div className="basket-line__details">
                <div className="basket-line__details__row">
                    <BasketLineTitle product={product} />
                    {product.price && <WishlistLinePrice product={product} />}
                </div>
                <div className="basket-line__details__row">
                    <BasketLineOptions product={product} />
                </div>
                <div className="basket-line__details__row">
                    <div className="basket-line__details__controls">
                        <div className={loadingIndicatorClasses}>
                            <Spinner name="three-bounce" fadeIn="none" />
                        </div>
                        {props.addBasketLine && (
                            <AddToBasketButton onAddToBasket={onAddToBasket} />
                        )}
                        <RemoveButton onRemoveLine={onRemoveLine} />
                    </div>
                </div>
            </div>
        </div>
    );
};
